
import { isValidEmail } from '@/utils/validation';

// List of known temporary/disposable email domains
const temporaryEmailDomains = [
  'tempmail.com', 'temp-mail.org', 'guerrillamail.com', 'mailinator.com', 
  'yopmail.com', 'sharklasers.com', 'trashmail.com', '10minutemail.com',
  'throwawaymail.com', 'getairmail.com', 'dispostable.com', 'mailnesia.com'
];

export interface FraudCheckResult {
  isFraud: boolean;
  reasons: string[];
}

export interface GeoLocationData {
  ip?: string;
  latitude?: number;
  longitude?: number;
  city?: string;
  country?: string;
  machineId?: string;
}

/**
 * Check if an email is from a known temporary/disposable email service
 */
export const isTemporaryEmail = (email: string): boolean => {
  if (!email) return false;
  
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return false;
  
  return temporaryEmailDomains.some(tempDomain => domain.includes(tempDomain));
};

/**
 * Detect fraud based on email, geolocation data, and other factors
 */
export const detectFraud = (email: string, geoData?: GeoLocationData): FraudCheckResult => {
  const result: FraudCheckResult = {
    isFraud: false,
    reasons: []
  };
  
  // Check for valid email format
  if (!isValidEmail(email)) {
    result.isFraud = true;
    result.reasons.push('Invalid email format');
  }
  
  // Check for temporary email
  if (isTemporaryEmail(email)) {
    result.isFraud = true;
    result.reasons.push('Temporary/disposable email detected');
  }
  
  // Check if geo-location data is missing
  if (!geoData?.ip) {
    result.reasons.push('IP address information missing');
  }
  
  // Check for VPN/proxy based on location data (simplified check for demo)
  if (geoData?.country === 'Unknown' || !geoData?.country) {
    result.reasons.push('Location information incomplete or suspicious');
  }
  
  return result;
};

/**
 * Get browser fingerprint as machine ID
 */
export const getMachineId = (): string => {
  const userAgent = navigator.userAgent;
  const screenRes = `${window.screen.width}x${window.screen.height}`;
  const colorDepth = window.screen.colorDepth;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const language = navigator.language;
  
  // Create a simplified fingerprint from these values
  const fingerprint = `${userAgent}|${screenRes}|${colorDepth}|${timezone}|${language}`;
  // Return a hash of the fingerprint
  return btoa(fingerprint).substring(0, 24);
};

/**
 * Get current geolocation information
 */
export const getGeoLocationData = async (): Promise<GeoLocationData> => {
  try {
    // Use a public API to get IP and location info
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    
    // Get machine ID
    const machineId = getMachineId();
    
    return {
      ip: data.ip,
      latitude: data.latitude,
      longitude: data.longitude,
      city: data.city,
      country: data.country_name,
      machineId
    };
  } catch (error) {
    console.error('Error fetching geolocation data:', error);
    return {
      machineId: getMachineId()
    };
  }
};
